if (!window.AutomaticGiftManager) {
  class AutomaticGiftManager {
    constructor() {
      this.pendingCart = null;
      this.isProcessing = false;
      this.onCartUpdate = this.onCartUpdate.bind(this);

      if (typeof theme !== 'undefined' && theme.pubsub?.subscribe) {
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate
        );
      }

      const reconcileCurrentCart = () => this.reconcileCurrentCart();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', reconcileCurrentCart, { once: true });
      }
      else {
        reconcileCurrentCart();
      }
    }

    onCartUpdate(event) {
      if (!event?.cart || event.source === 'automatic-gift') return;
      this.enqueue(event.cart);
    }

    enqueue(cart) {
      this.pendingCart = cart;
      if (this.isProcessing) return;

      this.isProcessing = true;
      this.drainQueue();
    }

    async drainQueue() {
      try {
        while (this.pendingCart) {
          const cart = this.pendingCart;
          this.pendingCart = null;

          try {
            await this.reconcile(cart);
          }
          catch (error) {
            this.reportError(error);
          }
        }
      }
      finally {
        this.isProcessing = false;
        if (this.pendingCart) this.enqueue(this.pendingCart);
      }
    }

    async reconcileCurrentCart() {
      try {
        this.enqueue(await this.fetchCart());
      }
      catch (error) {
        this.reportError(error);
      }
    }

    getProperty(item, key) {
      return item.properties?.[key] ?? '';
    }

    normalizeId(value) {
      const id = String(value || '').trim();
      return /^\d+$/.test(id) ? id : '';
    }

    normalizeQuantity(value) {
      return Math.max(1, parseInt(value, 10) || 1);
    }

    getMoneyValue(item, key) {
      const value = item[key];
      if (
        value === null
        || value === undefined
        || (typeof value === 'string' && value.trim() === '')
      ) return null;

      const amount = Number(value);
      return Number.isFinite(amount) && amount >= 0 ? amount : null;
    }

    hasRequiredGiftDiscount(item, freeQuantity) {
      const quantity = Math.max(0, parseInt(item.quantity, 10) || 0);
      const coveredQuantity = Math.min(
        quantity,
        Math.max(0, parseInt(freeQuantity, 10) || 0)
      );
      if (quantity === 0 || coveredQuantity === 0) return false;

      const finalLinePrice = this.getMoneyValue(item, 'final_line_price');
      if (finalLinePrice === null) return false;
      if (finalLinePrice === 0) return true;

      let originalLinePrice = this.getMoneyValue(item, 'original_line_price');
      let originalUnitPrice = this.getMoneyValue(item, 'original_price');

      if (originalUnitPrice === null && originalLinePrice !== null) {
        originalUnitPrice = originalLinePrice / quantity;
      }
      if (originalLinePrice === null && originalUnitPrice !== null) {
        originalLinePrice = originalUnitPrice * quantity;
      }
      if (originalLinePrice === null || originalUnitPrice === null) return false;

      const actualDiscount = Math.max(0, originalLinePrice - finalLinePrice);
      const requiredDiscount = originalUnitPrice * coveredQuantity;
      return actualDiscount >= requiredDiscount;
    }

    readPendingAdditions() {
      try {
        return JSON.parse(sessionStorage.getItem('automatic-gift-pending-additions') || '{}');
      }
      catch (error) {
        return {};
      }
    }

    writePendingAdditions(additions) {
      try {
        if (Object.keys(additions).length === 0) {
          sessionStorage.removeItem('automatic-gift-pending-additions');
        }
        else {
          sessionStorage.setItem('automatic-gift-pending-additions', JSON.stringify(additions));
        }
      }
      catch (error) {
        // Cart reconciliation still works when session storage is unavailable.
      }
    }

    getPendingAddition(ruleId) {
      const additions = this.readPendingAdditions();
      const addition = additions[ruleId];
      if (!addition) return 0;

      if (Date.now() - Number(addition.createdAt || 0) > 120000) {
        delete additions[ruleId];
        this.writePendingAdditions(additions);
        return 0;
      }

      return Math.max(0, parseInt(addition.quantity, 10) || 0);
    }

    clearPendingAdditions(ruleIds) {
      if (ruleIds.length === 0) return;

      const additions = this.readPendingAdditions();
      ruleIds.forEach((ruleId) => delete additions[ruleId]);
      this.writePendingAdditions(additions);
    }

    hasLegacySourceProperties(item) {
      return [
        '_automatic_gift_rule',
        '_automatic_gift_variant_id',
        '_automatic_gift_quantity'
      ].some((key) => this.getProperty(item, key) !== '');
    }

    getSourceMergeKey(item) {
      const legacyKeys = new Set([
        '_automatic_gift_rule',
        '_automatic_gift_variant_id',
        '_automatic_gift_quantity'
      ]);
      const properties = Object.entries(item.properties || {})
        .filter(([key]) => !legacyKeys.has(key))
        .sort(([left], [right]) => left.localeCompare(right));
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id
        || item.selling_plan
        || '';

      return JSON.stringify([item.variant_id, sellingPlanId, properties]);
    }

    getRule(sourceProductId, giftVariantId, giftQuantity, storedRule = '') {
      const ruleId = String(storedRule || `${sourceProductId}:${giftVariantId}:${giftQuantity}`);
      return {
        id: ruleId,
        key: `${ruleId}|${sourceProductId}:${giftVariantId}:${giftQuantity}`
      };
    }

    getPlan(plans, rule, sourceProductId, giftVariantId, giftQuantity) {
      if (!plans.has(rule.key)) {
        plans.set(rule.key, {
          ruleId: rule.id,
          sourceProductId,
          giftVariantId,
          giftQuantity,
          freeQuantity: 0,
          sourceItems: [],
          giftItems: []
        });
      }

      return plans.get(rule.key);
    }

    buildMutation(cart) {
      const plans = new Map();
      const items = cart.items || [];

      // The managed gift line owns the rule. Qualifying products remain ordinary
      // cart lines so Shopify can merge repeated additions of the same variant.
      items.forEach((item) => {
        if (this.getProperty(item, '_automatic_gift') !== 'true') return;

        const sourceProductId = this.normalizeId(this.getProperty(item, '_automatic_gift_for'));
        const giftVariantId = this.normalizeId(item.variant_id);
        const giftQuantity = this.normalizeQuantity(this.getProperty(item, '_automatic_gift_quantity'));

        if (!sourceProductId || !giftVariantId) {
          plans.set(`orphan:${item.key}`, {
            ruleId: '',
            sourceProductId: '',
            giftVariantId,
            giftQuantity,
            freeQuantity: 0,
            sourceItems: [],
            giftItems: [item]
          });
          return;
        }

        const rule = this.getRule(
          sourceProductId,
          giftVariantId,
          giftQuantity,
          this.getProperty(item, '_automatic_gift_rule')
        );
        const plan = this.getPlan(plans, rule, sourceProductId, giftVariantId, giftQuantity);
        plan.giftItems.push(item);
      });

      items.forEach((item) => {
        if (this.getProperty(item, '_automatic_gift') === 'true') return;

        const productId = this.normalizeId(item.product_id);
        if (!productId) return;

        plans.forEach((plan) => {
          if (plan.sourceProductId !== productId) return;

          plan.sourceItems.push(item);
          plan.freeQuantity += Math.max(0, parseInt(item.quantity, 10) || 0) * plan.giftQuantity;
        });
      });

      const updates = {};
      const resolvedRuleIds = new Set();

      plans.forEach((plan) => {
        const sourceGroups = new Map();
        plan.sourceItems.forEach((item) => {
          const mergeKey = this.getSourceMergeKey(item);
          if (!sourceGroups.has(mergeKey)) sourceGroups.set(mergeKey, []);
          sourceGroups.get(mergeKey).push(item);
        });

        sourceGroups.forEach((sourceItems) => {
          if (sourceItems.length < 2) return;

          const canonicalItem = sourceItems.find((item) => !this.hasLegacySourceProperties(item))
            || sourceItems[0];
          const quantity = sourceItems.reduce(
            (total, item) => total + Math.max(0, parseInt(item.quantity, 10) || 0),
            0
          );

          sourceItems.forEach((item) => {
            const nextQuantity = item === canonicalItem ? quantity : 0;
            if (item.quantity !== nextQuantity) updates[item.key] = nextQuantity;
          });
        });

        if (plan.freeQuantity === 0) {
          plan.giftItems.forEach((item) => {
            if (item.quantity !== 0) updates[item.key] = 0;
          });
          if (this.getPendingAddition(plan.ruleId) > 0) resolvedRuleIds.add(plan.ruleId);
          return;
        }

        // The free allowance is the default minimum while the gift is present.
        // The customer can add more units, and Shopify charges the excess.
        const selectedQuantity = plan.giftItems.reduce(
          (quantity, item) => quantity + Math.max(0, parseInt(item.quantity, 10) || 0),
          0
        );

        if (plan.giftItems.length > 1) {
          plan.giftItems.forEach((item, index) => {
            const quantity = index === 0 ? selectedQuantity : 0;
            if (item.quantity !== quantity) updates[item.key] = quantity;
          });
          return;
        }

        plan.giftItems.forEach((item) => {
          const hasRequiredDiscount = this.hasRequiredGiftDiscount(item, plan.freeQuantity);
          const pendingAddition = this.getPendingAddition(plan.ruleId);

          if (pendingAddition > 0) {
            if (!hasRequiredDiscount) {
              const quantity = Math.max(
                0,
                (parseInt(item.quantity, 10) || 0) - pendingAddition
              );
              if (item.quantity !== quantity) updates[item.key] = quantity;
            }
            resolvedRuleIds.add(plan.ruleId);
            return;
          }

          if (hasRequiredDiscount && item.quantity < plan.freeQuantity) {
            updates[item.key] = plan.freeQuantity;
          }
        });
      });

      return { updates, resolvedRuleIds: [...resolvedRuleIds] };
    }

    getSections() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections }
      }));
      return [...new Set(sections.filter(Boolean))];
    }

    getJsonUrl(url) {
      const [path, query] = String(url || '').split('?');
      const jsonPath = path.endsWith('.js') ? path : `${path}.js`;
      return query ? `${jsonPath}?${query}` : jsonPath;
    }

    async request(url, body) {
      const config = theme.utils.fetchConfig('javascript');
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(body);

      const response = await fetch(this.getJsonUrl(url), config);
      let parsedState;

      try {
        parsedState = await response.json();
      }
      catch (error) {
        throw new Error(`Automatic gift request returned invalid JSON (${response.status}).`);
      }

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Automatic gift cart update failed.');
      }

      return parsedState;
    }

    async fetchCart() {
      const response = await fetch(this.getJsonUrl(theme.routes.cart_url), {
        ...theme.utils.fetchConfig('json', 'GET')
      });

      if (!response.ok) {
        throw new Error(`Automatic gift cart fetch failed (${response.status}).`);
      }

      return response.json();
    }

    async applyMutation({ updates }) {
      const sections = this.getSections();
      const sectionPayload = sections.length > 0
        ? { sections, sections_url: window.location.pathname }
        : {};

      const parsedState = await this.request(theme.routes.cart_update_url, {
        updates,
        ...sectionPayload
      });

      const latestCart = await this.fetchCart();
      latestCart.sections = parsedState?.sections || {};
      return latestCart;
    }

    publishCartUpdate(cart) {
      if (!theme.pubsub || Object.keys(cart.sections || {}).length === 0) return;

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'automatic-gift',
        cart
      });
    }

    async reconcile(cart) {
      let latestCart = cart;
      let changed = false;

      for (let pass = 0; pass < 4; pass += 1) {
        const mutation = this.buildMutation(latestCart);
        if (Object.keys(mutation.updates).length === 0) {
          this.clearPendingAdditions(mutation.resolvedRuleIds);
          if (changed) this.publishCartUpdate(latestCart);
          return;
        }

        latestCart = await this.applyMutation(mutation);
        this.clearPendingAdditions(mutation.resolvedRuleIds);
        changed = true;
      }

      const remainingMutation = this.buildMutation(latestCart);
      if (Object.keys(remainingMutation.updates).length > 0) {
        throw new Error('Automatic gift cart did not reach a stable state.');
      }

      if (changed) this.publishCartUpdate(latestCart);
    }

    reportError(error) {
      console.error('[Automatic gift]', error);
      document.dispatchEvent(new CustomEvent('automatic-gift:error', {
        detail: { error }
      }));
    }
  }

  window.AutomaticGiftManager = new AutomaticGiftManager();
}
